import React from "react";
import { Row, Col, Card, Tabs, Badge, Tooltip } from "antd";
import { MemberProfile } from "../../../../model/members/MemberProfile";
import { DirectDebitRunPayment } from "../../../../model/payments/DirectDebitRunPayment";
import { PointOfSaleTransaction } from "../../../../model/pointofsale/PointOfSaleTransaction";
import { MembershipCategory } from "../../../../model/members/MembershipCategory";
import { HealthFormSummary } from "../../../../model/members/HealthFormSummary";
import { QuestionnaireSchema } from "../../forms/QuestionnaireSchema";
import { ProfileAlert, AlertActionTarget } from "./alerts";
import { MemberProfileMode } from "./modeConfig";

import IdentityHeader from "./IdentityHeader";
import MemberPhotoCard from "./MemberPhotoCard";
import NotesCard from "./NotesCard";
import MetadataCard from "./MetadataCard";
import { MemberMetadataTypeDescriptor } from "../../../../model/members/MemberMetadataTypeDescriptor";
import AlertsSummary from "./AlertsSummary";
import StatusValidityCard from "./StatusValidityCard";
import CheckinSummary from "./CheckinSummary";
import QuickActions from "./QuickActions";
import PersonalDetailsForm from "./PersonalDetailsForm";

import DirectDebitInput from "../DirectDebitInput";
import DirectDebitPaymentsView from "../DirectDebitPaymentsView";
import RecurringMemberItemsPanel from "../RecurringMemberItemsPanel";
import JoiningFeeManager from "../JoiningFeeManager";
import JointMembershipManager from "../JointMembershipManager";
import PointOfSaleTransactionView from "../PointOfSaleTransactionView";
import MemberCheckinsView from "../MemberCheckinsView";
import MemberCancellationsView from "../MemberCancellationsView";
import HealthResultsTab from "../../health/HealthResultsTab";

interface LayoutTwoColumnProps {
    member: MemberProfile;
    mode: MemberProfileMode;
    selfServiceMode: boolean;
    // Create/onboarding entry — drives the PersonalDetailsForm CTA (Quick Save).
    isCreating?: boolean;
    membershipCategories: MembershipCategory[];
    // Backend catalogue driving the staff-only Metadata section. Empty in
    // self-service (never fetched / rendered there).
    metadataTypes: MemberMetadataTypeDescriptor[];
    memberPayments: DirectDebitRunPayment[];
    pointOfSaleTransactions: PointOfSaleTransaction[];
    alerts: ProfileAlert[];
    healthSummary: HealthFormSummary | null;
    healthSchema: QuestionnaireSchema;
    healthLoading: boolean;
    // Staff may record a review; members see reviews read-only.
    healthCanReview: boolean;
    // Coloured dot + hover tooltip on the Health tab. `show` drives the dot;
    // `tooltip` explains the issue (no questionnaire, or flagged answers awaiting
    // review) and is only surfaced while `show` is true.
    healthWarning: { show: boolean; tooltip: string };
    activeTab: string;
    onTabChange: (key: string) => void;
    checkinRefreshKey: number;
    checkedInAt: Date | null;
    onCheckIn: () => void;
    checkingIn: boolean;
    checkInDisabled: boolean;
    onUpdateMember: (values: Partial<MemberProfile>) => void;
    onSaveNote: (notes: string) => void;
    savingNote: boolean;
    onMemberUpdated: () => void;
    onPhotoUploaded: () => void;
    onPosTransactionUpdated: () => void;
    onHealthSummaryChanged: () => void;
    // Onboarding only — reports the direct-debit mandate entered during create so
    // it can be submitted with the member-create request.
    onDirectDebitChange?: (values: { accountHolderName: string; bankName: string; accountNumber: string; sortCode: string }) => void;
    // Onboarding only — whether a fully-valid DD mandate has been entered. Feeds
    // the Save-button gate for recurring memberships (annual/one-off don't need it).
    directDebitComplete?: boolean;
    // Client-specific direct-debit cancellation terms (empty => none configured).
    // Shown read-only once a mandate is on file, or with a required tick box while
    // a blank mandate is being set up.
    cancellationNotice?: string;
    // Fired when the Billing tab's own DD panel saves a mandate, so the screen can
    // re-read mandate state (the profile-completion banner depends on it).
    onDirectDebitSaved?: () => void;
}

// Direction C — "at-a-glance rail + workspace". Identity header + admin-only
// staff notes span full width; below, a narrow left summary rail (alerts,
// status/validity, activity, quick actions) sits beside a wide right workspace
// (the editable details form + tabbed billing / transactions / activity /
// health). Both columns stack to full width under the lg breakpoint.
const LayoutTwoColumn: React.FC<LayoutTwoColumnProps> = (props) => {
    const {
        member, mode, selfServiceMode, isCreating, membershipCategories, metadataTypes, memberPayments, pointOfSaleTransactions,
        alerts, healthSummary, healthSchema, healthLoading, healthCanReview, healthWarning, activeTab, onTabChange,
        checkinRefreshKey, checkedInAt, onCheckIn, checkingIn, checkInDisabled, onUpdateMember, onSaveNote,
        savingNote, onMemberUpdated, onPhotoUploaded, onPosTransactionUpdated, onHealthSummaryChanged,
        onDirectDebitChange, directDebitComplete, cancellationNotice, onDirectDebitSaved,
    } = props;

    const isJoint = !!member.membershipCategory?.isJointMembership;

    const tabs: { key: string; label: React.ReactNode; children: React.ReactNode }[] = [];

    if (mode.pos) {
        tabs.push({
            key: "tx",
            label: "Transactions",
            children: <PointOfSaleTransactionView memberId={member.membershipId} memberName={member.name} canEdit={mode.posEdit} />,
        });
    }

    tabs.push({
        key: "billing",
        label: "Billing",
        children: (
            <div className="ft-card-stack">
                {/* Direct debit is shown while creating a blank member with the internal
                    Save button hidden and no fetch (isCreating). Self-service onboarding
                    makes it editable and lifts the entered mandate to the create request
                    (onDirectDebitChange); admin create leaves it read-only (DD added after
                    the member exists). Other billing panels key off a membershipId that
                    doesn't exist yet, so they stay hidden until the member is saved. */}
                {mode.ddMandate && (
                    <DirectDebitInput
                        membershipId={member.membershipId}
                        selfServiceMode={selfServiceMode}
                        canEdit={isCreating ? selfServiceMode : (selfServiceMode ? true : mode.ddMandateEdit)}
                        maskDetails={!isCreating && !selfServiceMode && !mode.ddMandateEdit}
                        isCreating={isCreating}
                        onCreateChange={onDirectDebitChange}
                        cancellationNotice={cancellationNotice}
                        onSaved={onDirectDebitSaved}
                    />
                )}
                {mode.payments && !isCreating && <DirectDebitPaymentsView payments={memberPayments} />}
                {mode.recurring && !isCreating && <RecurringMemberItemsPanel membershipId={member.membershipId} />}
                {mode.joiningFee && !isCreating && (
                    <JoiningFeeManager
                        membershipId={member.membershipId}
                        pointOfSaleTransactions={pointOfSaleTransactions}
                        onTransactionUpdated={onPosTransactionUpdated}
                    />
                )}
                {mode.editMembership && isJoint && (
                    <JointMembershipManager member={member} onUpdate={onUpdateMember} />
                )}
            </div>
        ),
    });

    if (mode.checkins || mode.cancellations) {
        tabs.push({
            key: "activity",
            label: "Activity",
            children: (
                <div className="ft-card-stack">
                    {mode.checkins && <MemberCheckinsView membershipId={member.membershipId} />}
                    {mode.cancellations && (
                        <MemberCancellationsView
                            membershipId={member.membershipId}
                            canManage={mode.cancellationsEdit}
                        />
                    )}
                </div>
            ),
        });
    }

    if (mode.health) {
        tabs.push({
            key: "health",
            label: (
                <Tooltip title={healthWarning.show ? healthWarning.tooltip : undefined}>
                    <span data-testid="health-tab-label">
                        <Badge dot={healthWarning.show}>Health</Badge>
                    </span>
                </Tooltip>
            ),
            children: (
                <HealthResultsTab
                    membershipId={member.membershipId}
                    schema={healthSchema}
                    summary={healthSummary}
                    loading={healthLoading}
                    canReview={healthCanReview}
                    selfServiceMode={selfServiceMode}
                    onHealthSummaryChanged={onHealthSummaryChanged}
                />
            ),
        });
    }

    // Guard the controlled active key: the default ("tx") is only present when
    // mode.pos is set, so fall back to the first available tab if the requested
    // key isn't in the list (avoids antd rendering a blank tab body).
    const effectiveActiveTab = tabs.some(t => t.key === activeTab) ? activeTab : tabs[0]?.key;

    return (
        <div className="ft-card-stack">
            <IdentityHeader
                member={member}
                mode={mode}
                selfServiceMode={selfServiceMode}
                onCheckIn={onCheckIn}
                checkingIn={checkingIn}
                checkInDisabled={checkInDisabled}
                onPosTransactionUpdated={onPosTransactionUpdated}
            />

            {mode.notes && <NotesCard member={member} onSave={onSaveNote} saving={savingNote} />}

            <Row gutter={[14, 14]}>
                <Col xs={24} lg={8}>
                    <div className="ft-card-stack">
                        <MemberPhotoCard member={member} mode={mode} onPhotoUploaded={onPhotoUploaded} />
                        <AlertsSummary alerts={alerts} onAction={(target: AlertActionTarget) => onTabChange(target)} />
                        <StatusValidityCard member={member} />
                        {mode.checkins && (
                            <CheckinSummary
                                membershipId={member.membershipId}
                                mode={mode}
                                onCheckIn={onCheckIn}
                                checkingIn={checkingIn}
                                checkInDisabled={checkInDisabled}
                                refreshKey={checkinRefreshKey}
                                checkedInAt={checkedInAt}
                            />
                        )}
                        {mode.actions && (
                            <QuickActions
                                member={member}
                                mode={mode}
                                pointOfSaleTransactions={pointOfSaleTransactions}
                                membershipCategories={membershipCategories}
                                onCheckIn={onCheckIn}
                                checkingIn={checkingIn}
                                checkInDisabled={checkInDisabled}
                                onMemberUpdated={onMemberUpdated}
                                onPosTransactionUpdated={onPosTransactionUpdated}
                            />
                        )}
                        {mode.metadata && !isCreating && (
                            <MetadataCard
                                member={member}
                                metadataTypes={metadataTypes}
                                onSave={(name, value) =>
                                    onUpdateMember({ metadata: { ...member.metadata, [name]: value } })
                                }
                            />
                        )}
                    </div>
                </Col>
                <Col xs={24} lg={16}>
                    <div className="ft-card-stack">
                        <PersonalDetailsForm
                            member={member}
                            mode={mode}
                            membershipCategories={membershipCategories}
                            onUpdate={onUpdateMember}
                            isCreating={isCreating}
                            selfServiceMode={selfServiceMode}
                            directDebitComplete={directDebitComplete}
                        />
                        <Card size="small" styles={{ body: { padding: "4px 16px 16px" } }}>
                            <div data-testid="member-profile-tabs">
                                <Tabs activeKey={effectiveActiveTab} onChange={onTabChange} items={tabs} />
                            </div>
                        </Card>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default LayoutTwoColumn;
